import { useCallback, useEffect, useRef } from 'react';

/**
 * Sequential text-to-speech narration with duration-synced text reveal.
 *
 * Sentences are enqueued as the stream produces them. Each one is synthesized
 * concurrently but played strictly in order, and its characters are revealed at
 * a rate derived from the audio's own duration so the text tracks the voice.
 *
 * This is the half of the old useStreamingResponse that the technical interview
 * could not reuse: it needed the SSE transport but had its own playback, so it
 * reached past the hook to readSSEStream and kept a second copy of all of this.
 * Splitting playback out is what lets both modules share one implementation.
 *
 * Transport-independent by design — it never touches fetch or SSE. Callers feed
 * it sentences from whatever source they like.
 *
 * @param {Object} options
 * @param {Function} options.generateAudio - text -> Promise<Audio|null> (from useTextToSpeech)
 * @param {Function} options.onUpdate - receives partial message updates to render
 * @param {Function} options.onSpeakingChange - called with true/false as playback starts/stops
 * @param {number} options.sentencePauseMs - silence inserted between sentences
 */
const useAudioNarration = (options = {}) => {
  const {
    generateAudio,
    onUpdate,
    onSpeakingChange,
    // Each sentence is synthesized separately, so the clips carry no silence
    // at their edges and previously ran straight into one another -- the
    // interviewer sounded like it was reading a list rather than talking.
    // ~350ms is a conversational inter-sentence pause; careful speech runs
    // longer, but an interviewer that pauses a full second sounds hesitant.
    sentencePauseMs = 350,
  } = options;

  // Callbacks live in refs so a caller re-rendering with new closures does not
  // invalidate an in-flight narration.
  const onUpdateRef = useRef(onUpdate);
  const onSpeakingChangeRef = useRef(onSpeakingChange);
  const generateAudioRef = useRef(generateAudio);

  useEffect(() => {
    onUpdateRef.current = onUpdate;
  }, [onUpdate]);
  useEffect(() => {
    onSpeakingChangeRef.current = onSpeakingChange;
  }, [onSpeakingChange]);
  useEffect(() => {
    generateAudioRef.current = generateAudio;
  }, [generateAudio]);

  // Per-turn state. Refs rather than state: these mutate many times per second
  // during playback and nothing renders off them directly.
  const sentencesRef = useRef([]);
  const queueRef = useRef([]);
  const audioSequenceRef = useRef(0);
  const currentAudioIndexRef = useRef(0);
  const currentDisplayIndexRef = useRef(-1);
  const isPlayingRef = useRef(false);

  // Every Audio element started, so playback can actually be stopped. This
  // deliberately does NOT go through useTextToSpeech, which owns a single
  // element; a queue needs to track all of them.
  const activeAudioRef = useRef([]);
  // Set when the caller stops narration: chunks whose synthesis resolves after
  // that point must not start playing.
  const cancelledRef = useRef(false);
  // Timers from the reveal schedule, so stop() can cancel pending reveals
  // instead of letting them fire against a torn-down turn.
  const timersRef = useRef([]);
  // True while waiting out the gap between two sentences. finish() must not
  // treat that quiet moment as "not playing" and jump the queue forward.
  const pausePendingRef = useRef(false);

  const clearTimers = useCallback(() => {
    timersRef.current.forEach(clearTimeout);
    timersRef.current = [];
  }, []);

  const schedule = useCallback((fn, delay) => {
    const id = setTimeout(fn, delay);
    timersRef.current.push(id);
    return id;
  }, []);

  /** Push the currently-visible prefix of the transcript to the caller. */
  const emitVisible = useCallback(() => {
    const visibleText = sentencesRef.current
      .filter((_, index) => index <= currentDisplayIndexRef.current)
      .map((s) => {
        if (s.characters) {
          return s.characters
            .filter((c) => c.isVisible)
            .map((c) => c.char)
            .join('');
        }
        return s.text;
      })
      .join(' ');

    onUpdateRef.current?.({
      content: visibleText,
      sentences: [...sentencesRef.current],
      currentDisplayIndex: currentDisplayIndexRef.current,
    });
  }, []);

  /** Reveal one sentence character by character, paced to its audio duration. */
  const startReveal = useCallback(
    (sentenceIndex, audioElement) => {
      const sentence = sentencesRef.current[sentenceIndex];
      if (!sentence) return;

      const chars = sentence.text.split('');
      const totalChars = chars.length;

      // `duration` is the clip's INTRINSIC length and does not shrink when the
      // element plays faster, so a 6s clip at playbackRate 1.2 ends after 5s of
      // wall clock. Pacing the reveal off the raw duration leaves the sentence
      // still typing when the next one starts speaking. Divide by the rate to
      // get elapsed time, which is what the reveal schedule actually runs on.
      const rate = audioElement?.playbackRate || 1;

      const setup = (intrinsicMs) => {
        const timePerChar = intrinsicMs / rate / totalChars;
        // Clamped so a very short clip does not dump the sentence instantly and
        // a very long one does not crawl.
        const finalTimePerChar = Math.max(15, Math.min(80, timePerChar));

        sentence.characters = chars.map((char, index) => ({
          char,
          isVisible: false,
          revealTime: index * finalTimePerChar,
        }));

        currentDisplayIndexRef.current = sentenceIndex;
        emitVisible();

        chars.forEach((_, charIndex) => {
          schedule(() => {
            if (sentence.characters?.[charIndex]) {
              sentence.characters[charIndex].isVisible = true;
              emitVisible();
            }
          }, charIndex * finalTimePerChar);
        });
      };

      // Fallback when metadata never arrives. ~50ms/char is a natural speaking
      // rate, i.e. an intrinsic length, so `setup` divides it by the rate too.
      const estimate = () => Math.max(1500, totalChars * 50);

      if (audioElement?.duration && !isNaN(audioElement.duration)) {
        setup(audioElement.duration * 1000);
        return;
      }

      const handleLoadedMetadata = () => {
        if (audioElement.duration && !isNaN(audioElement.duration)) {
          setup(audioElement.duration * 1000);
        } else {
          setup(estimate());
        }
        audioElement.removeEventListener?.('loadedmetadata', handleLoadedMetadata);
      };

      audioElement?.addEventListener?.('loadedmetadata', handleLoadedMetadata);

      // Metadata may never arrive (a stub element, a failed decode); fall back
      // to a length estimate rather than never revealing the sentence.
      schedule(() => {
        if (!sentence.characters) setup(estimate());
      }, 500);
    },
    [emitVisible, schedule]
  );

  /** Reveal a sentence in full, immediately — used when its audio is unusable. */
  const revealWholeSentence = useCallback(
    (index) => {
      const sentence = sentencesRef.current[index];
      if (!sentence) return;
      sentence.characters = sentence.text.split('').map((char) => ({ char, isVisible: true }));
      currentDisplayIndexRef.current = index;
      emitVisible();
    },
    [emitVisible]
  );

  /** Play whatever is next in the queue, skipping chunks that failed. */
  const playNext = useCallback(() => {
    if (isPlayingRef.current || cancelledRef.current) return;

    // Skip failed chunks, showing their text immediately so nothing is lost.
    while (
      queueRef.current.some(
        (chunk) => chunk.index === currentAudioIndexRef.current && chunk.failed
      )
    ) {
      if (currentAudioIndexRef.current <= sentencesRef.current.length - 1) {
        revealWholeSentence(currentAudioIndexRef.current);
      }
      currentAudioIndexRef.current += 1;
    }

    const nextChunk = queueRef.current.find(
      (chunk) => chunk.index === currentAudioIndexRef.current && chunk.audio
    );

    if (nextChunk) {
      isPlayingRef.current = true;
      onSpeakingChangeRef.current?.(true);

      startReveal(currentAudioIndexRef.current, nextChunk.audio);

      const advance = ({ reveal }) => {
        isPlayingRef.current = false;
        currentAudioIndexRef.current += 1;
        if (reveal && currentAudioIndexRef.current <= sentencesRef.current.length - 1) {
          currentDisplayIndexRef.current = currentAudioIndexRef.current;
          emitVisible();
        }

        // A beat of silence before the next sentence. Delaying playNext rather
        // than padding the audio keeps the reveal in step: startReveal runs
        // inside playNext, so text and voice shift together.
        //
        // Only between sentences -- pausing after the last one would hold
        // isSpeaking true and delay the candidate's mic by the same amount.
        const hasMore = queueRef.current.some(
          (chunk) => chunk.index >= currentAudioIndexRef.current && !chunk.failed
        );

        if (hasMore && sentencePauseMs > 0) {
          pausePendingRef.current = true;
          schedule(() => {
            pausePendingRef.current = false;
            playNext();
          }, sentencePauseMs);
          return;
        }

        playNext();
      };

      nextChunk.audio.onended = () => advance({ reveal: false });
      nextChunk.audio.onerror = () => advance({ reveal: true });
      // Wrapped rather than chained directly: play() returns undefined on older
      // Safari, and `undefined.catch` would throw straight out of the queue.
      Promise.resolve(nextChunk.audio.play?.()).catch(() => advance({ reveal: true }));
      return;
    }

    // Nothing ready. If the whole queue is behind us, playback is done.
    if (queueRef.current.every((chunk) => chunk.index < currentAudioIndexRef.current)) {
      onSpeakingChangeRef.current?.(false);

      const remaining = sentencesRef.current.length - 1 - currentDisplayIndexRef.current;
      for (let i = 1; i <= remaining; i++) {
        const target = currentDisplayIndexRef.current + i;
        schedule(() => {
          if (target <= sentencesRef.current.length - 1) {
            currentDisplayIndexRef.current = target;
            emitVisible();
          }
        }, 500 * i);
      }
    }
  }, [emitVisible, revealWholeSentence, schedule, sentencePauseMs, startReveal]);

  /** Synthesize one sentence and slot the result into its queue placeholder. */
  const synthesize = useCallback(
    async (text, sequenceIndex) => {
      const markFailed = () => {
        const item = queueRef.current.find((i) => i.index === sequenceIndex);
        if (item) item.failed = true;
        playNext();
      };

      if (!generateAudioRef.current || !text.trim()) {
        markFailed();
        return;
      }

      try {
        const audio = await generateAudioRef.current(text.trim());
        const item = queueRef.current.find((i) => i.index === sequenceIndex);
        if (!item) return;

        if (!audio) {
          item.failed = true;
          playNext();
          return;
        }

        if (cancelledRef.current) {
          // Narration was stopped while this was synthesizing — drop it rather
          // than talking over whatever replaced the interview.
          item.failed = true;
          return;
        }

        item.audio = audio;
        activeAudioRef.current.push(audio);
        playNext();
      } catch (error) {
        console.error('Error generating audio chunk:', error);
        markFailed();
      }
    },
    [playNext]
  );

  /** Queue a complete sentence for narration. */
  const enqueue = useCallback(
    (text) => {
      if (!text || !text.trim()) return;

      sentencesRef.current.push({
        text,
        isVisible: false,
        audioIndex: audioSequenceRef.current,
        isAudioReady: false,
      });

      const sequence = audioSequenceRef.current;
      audioSequenceRef.current += 1;

      queueRef.current.push({
        index: sequence,
        audio: null,
        text,
        sentenceIndex: sentencesRef.current.length - 1,
      });

      synthesize(text, sequence);
    },
    [synthesize]
  );

  /**
   * No more sentences are coming. Starts playback if it has not begun, and
   * makes sure the transcript ends up fully visible even when no audio ever
   * arrived.
   */
  const finish = useCallback(() => {
    // A pending inter-sentence pause is not idleness. Without this guard a
    // stream ending during the gap calls playNext early, cutting the pause and
    // making the last sentence run into the previous one.
    if (pausePendingRef.current) return;

    if (!isPlayingRef.current && queueRef.current.length > 0) {
      playNext();
    }

    if (isPlayingRef.current) return;

    // A chunk still being synthesized has `audio === null` but is NOT failed —
    // pending, not absent. Treating it as absent fires this safeguard on every
    // normal stream end: the answer is revealed and isSpeaking cleared, then the
    // blob resolves and resets the reveal, so the text visibly flashes,
    // collapses and re-types itself.
    const hasPending = queueRef.current.some(
      (chunk) => chunk.index >= currentAudioIndexRef.current && !chunk.failed
    );
    if (hasPending) return;

    onSpeakingChangeRef.current?.(false);

    for (let i = Math.max(0, currentDisplayIndexRef.current + 1); i < sentencesRef.current.length; i++) {
      const sentence = sentencesRef.current[i];
      if (sentence) {
        sentence.characters = sentence.text.split('').map((char) => ({ char, isVisible: true }));
      }
    }
    if (sentencesRef.current.length > 0) {
      currentDisplayIndexRef.current = sentencesRef.current.length - 1;
      emitVisible();
    }
  }, [emitVisible, playNext]);

  /** Stop playback and silence anything already started or in flight. */
  const stop = useCallback(() => {
    cancelledRef.current = true;
    // Defensive, and deliberately not covered by a test: clearTimers() already
    // cancels the pause timer, and finish() short-circuits on `hasPending`
    // before the flag is read, so no current path observes a stale `true`.
    // Left in because it gates finish(), and a reordering of those guards would
    // turn stale state here into a transcript that never finishes revealing.
    pausePendingRef.current = false;
    clearTimers();
    activeAudioRef.current.forEach((audio) => {
      try {
        audio.pause?.();
        audio.currentTime = 0;
        audio.onended = null;
        audio.onerror = null;
      } catch (err) {
        // Element may already be detached; nothing to do.
      }
    });
    activeAudioRef.current = [];
    isPlayingRef.current = false;
  }, [clearTimers]);

  /** Begin a new turn, discarding the previous one's state. */
  const reset = useCallback(() => {
    clearTimers();
    sentencesRef.current = [];
    queueRef.current = [];
    audioSequenceRef.current = 0;
    currentAudioIndexRef.current = 0;
    currentDisplayIndexRef.current = -1;
    isPlayingRef.current = false;
    activeAudioRef.current = [];
    cancelledRef.current = false;
    pausePendingRef.current = false;
  }, [clearTimers]);

  const getSentences = useCallback(() => [...sentencesRef.current], []);

  useEffect(() => {
    return () => {
      clearTimers();
    };
  }, [clearTimers]);

  return {
    enqueue,
    finish,
    stop,
    reset,
    getSentences,
  };
};

export default useAudioNarration;
